import isEqual from "lodash/isEqual";
import { MultiActivityFormStoreProvider } from "PFApp/stores/activity_form/multi_roles_form";
import { Id } from "PFCore/types";
import { PropsWithChildren, useState } from "react";
import { createStore } from "zustand";
import { immer } from "zustand/middleware/immer";

import { ManageRolesStoreType } from "./manage_roles_store.types";
import { ManageRolesStoreContext } from "./manage_roles_store_context";

export const ManageRolesStoreProvider = ({ children }: PropsWithChildren) => {
  const [store] = useState(
    createStore<ManageRolesStoreType>()(
      immer((set, get) => ({
        currentPage: 1,
        pendingEdits: {},
        actions: {
          setCurrentPage: (page) =>
            set((state) => {
              state.currentPage = page;
            }),
          addPendingEdit: (
            roleId: Id,
            fieldName: string,
            value: unknown,
            previousValue: unknown,
            page: number
          ) =>
            set((state) => {
              const key = `${roleId}.${fieldName}`;
              const existing = state.pendingEdits[key];
              if (existing && existing.status === "pending" && isEqual(existing.value, value)) {
                return;
              }
              // If an inflight request is in progress for this key and a new edit arrives, we overwrite the
              // entry with status "pending" so it will be retried. previousValue is preserved from the
              // original edit so that rollback on failure reverts to the correct pre-edit server value.
              state.pendingEdits[key] = {
                roleId,
                fieldName,
                value,
                previousValue: existing ? existing.previousValue : previousValue,
                status: "pending",
                page
              };
            }),
          markInflight: (keys: string[]) =>
            set((state) => {
              keys.forEach((key) => {
                if (state.pendingEdits[key]) {
                  state.pendingEdits[key].status = "inflight";
                }
              });
            }),
          clearEdits: (keys: string[]) =>
            set((state) => {
              keys.forEach((key) => {
                delete state.pendingEdits[key];
              });
            }),
          revertEdit: (key: string) =>
            set((state) => {
              delete state.pendingEdits[key];
            }),
          // Intentionally an action (not a selector) — reads state imperatively via get() without
          // subscribing, so callers don't re-render on every pendingEdits mutation.
          hasPendingEdits: () => Object.keys(get().pendingEdits).length > 0
        }
      }))
    )
  );

  return (
    <ManageRolesStoreContext.Provider value={store}>
      <MultiActivityFormStoreProvider>{children}</MultiActivityFormStoreProvider>
    </ManageRolesStoreContext.Provider>
  );
};
